/**
 * Date rendering helpers that respect the admin-configured timezone policy.
 *
 * The backend dashboard returns:
 *   delivery_schedule.display_mode        — 'local' | 'operating'
 *   delivery_schedule.operating_timezone  — IANA timezone name (e.g., 'America/New_York')
 *
 * Most calendar dates are returned as `YYYY-MM-DD` strings. We always pin
 * those to local-noon (`+T12:00:00`) so they never shift across timezones —
 * regardless of display_mode, the calendar day is the same. The display_mode
 * affects rendering only when the source value is a real timestamp (e.g., a
 * `created_at` ISO with timezone), which is the case where we'd otherwise
 * shift days under the user's browser tz.
 */

const DATE_OPTS = { year: 'numeric', month: 'numeric', day: 'numeric' };

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    // Date-only strings (YYYY-MM-DD) — pin to noon so toLocaleString in any
    // timezone gives the correct calendar day.
    if (value.length === 10) return new Date(`${value}T12:00:00`);
    return new Date(value);
  }
  return new Date(value);
};

/**
 * Render a date string respecting the admin's display mode and operating tz.
 *
 * @param {string|Date} value          source value
 * @param {object}      schedule       delivery_schedule from /client-portal/dashboard
 * @param {object}      [opts]         optional Intl.DateTimeFormat options to extend defaults
 * @returns {string}                   formatted date (or '' if value falsy)
 */
export const formatScheduleDate = (value, schedule, opts = {}) => {
  const d = toDate(value);
  if (!d) return '';
  const display = schedule?.display_mode || 'local';
  const tz = schedule?.operating_timezone;
  const options = { ...DATE_OPTS, ...opts };
  if (display === 'operating' && tz) {
    options.timeZone = tz;
  }
  return d.toLocaleDateString('en-US', options);
};

/**
 * Render a localized weekday for a date. Useful when we want the weekday name
 * itself to match the operating timezone (e.g., showing "Saturday" for a
 * delivery that falls on Saturday in the bakery's operating tz).
 */
export const formatScheduleWeekday = (value, schedule) =>
  formatScheduleDate(value, schedule, { weekday: 'long' }).split(',')[0] ||
  formatScheduleDate(value, schedule, { weekday: 'long' });
